import { FC, useCallback, useEffect, useState } from 'react';
import { NoticeLayoutProps } from './NoticeLayout.types';
import {
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import PagePath from '../../components/PagePath';
import UserWebInfo from '../../components/UserWebInfo/UserWebInfo';
import { STYLE_VARS } from '../../constants/style-vars';
import { IS_WEB } from '../../constants/platform';
import { addBase64Prefix } from '../../services/addBase64Prefix';
import { filter_button, reset_icon } from '../../assets/base64';
import { FONTS } from '../../constants';
import PageTitle from '../../components/PageTitle/PageTitle';
import { NavigationService } from '../../services/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../services/redux';
import { NoticeFilter, setLoadingNotice, setNoticeFilter, setNotices, setTotal } from '../../services/redux/reducers/NoticeSlice';
import NoticesApi from '../../services/api/notices.api';
import { decode } from 'html-entities';

type NoticeCategory = {
  id: number;
  name: string;
  slug: string;
  description: string;
  parent: number;
  child?: NoticeCategory[];
};

const NoticeLayout: FC<NoticeLayoutProps> = ({
  children,
  haveGoBackButton = false,
}) => {
  const noticeFilters = [
    {
      name: 'CLUB EVENTS',
      value: 'clubEvents',
    },
    {
      name: 'FOOD & BAVERAGES',
      value: 'foodAndBaverages',
    },
  ];
  const dispatch = useDispatch();
  const { noticeFilter } = useSelector((state: RootState) => state.noticeReducer);

  const [noticeCategories, setNoticeCategories] = useState<NoticeCategory[]>([]);
  const [filterSelected, setFilterSelected] = useState<NoticeCategory[]>([]);

  const getNotices = useCallback(
    async (props: NoticeFilter) => {
      try {
        dispatch(setLoadingNotice(true));
        const res = await NoticesApi.getNotices(props);
        dispatch(setNotices(res.data.notices));
        dispatch(setTotal(res.data.total));
      } catch (error: any) {
        console.log(error);
      } finally {
        dispatch(setLoadingNotice(false));
      }
    }, [dispatch]
  )

  const handleGetNoticeCategoriesData = useCallback(async () => {
    try {
      const res = await NoticesApi.getNoticeCategories({});
      const data = res.data;
      let categories: NoticeCategory[] = [];
      for (let i = 0; i < data.length; i++) {
        let category: NoticeCategory = {
          ...data[i],
          child: [],
        };
        if (category.parent === 0) {
          category.child = data.filter((e) => e.parent === category.id);
          categories.push(category);
        }
      }
      setNoticeCategories(categories);
    } catch (error: any) {
      console.log(error);
    } finally {
    }
  }, [setNoticeCategories]);

  useEffect(() => {
    const filters = filterSelected.filter(
      (filter) => !filter.child || filter.child.length === 0
    );
    const slugArray = filters.map((obj) => obj.slug);
    const resultString = slugArray.join(',');
    if (resultString !== noticeFilter.cats) {
      dispatch(setNotices([]));
      getNotices({
        ...noticeFilter,
        cats: resultString,
        page: 1,
      });
      dispatch(
        setNoticeFilter({
          ...noticeFilter,
          cats: resultString,
          page: 1,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSelected, dispatch, getNotices]);

  const handleFilterSelect = useCallback(
    (selected: NoticeCategory) => {
      if (filterSelected.includes(selected)) {
        const arr = filterSelected.filter((filter) => filter !== selected);
        setFilterSelected(arr);
      } else {
        const arr = [...filterSelected, selected];
        setFilterSelected(arr);
      }
    },
    [filterSelected]
  );
  const handleFilterReset = useCallback(() => {
    setFilterSelected([]);
    getNotices({
      ...noticeFilter,
      cats: '',
      page: 1,
    });
    dispatch(
      setNoticeFilter({
        ...noticeFilter,
        cats: '',
        page: 1,
      })
    );
  }, [getNotices, noticeFilter, dispatch]);

  useEffect(() => {
    handleGetNoticeCategoriesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View
        style={{
          maxWidth: 1250,
          width: '90%',
          marginHorizontal: 'auto',
          marginTop: 10,
          minHeight: 500,
        }}
      >
        <PagePath pathSteps={[
          {
            name: 'Member Area',
            onPress: () => {
              NavigationService.push('Root', {
                screen: 'HomeTab',
              });
            },
          },
          { name: 'Notices' }
        ]} />
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginVertical: 30,
          }}
        >
          <View style={{ width: '20%', minWidth: 230, marginRight: 20 }}>
            <UserWebInfo />
            <View style={{ marginTop: 60 }}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  borderBottomWidth: 0.5,
                  borderBottomColor: STYLE_VARS.fccDarkGrey,
                  paddingBottom: 15,
                  marginBottom: 15,
                }}
              >
                <Image
                  source={
                    IS_WEB
                      ? addBase64Prefix(filter_button)
                      : require('../../assets/phone-white.png')
                  }
                  style={{ width: 24, height: 24, marginRight: 8 }}
                />
                <Text
                  style={{
                    fontSize: 16,
                    letterSpacing: 2,
                    fontFamily: FONTS.IowanOldStyleBTRoman,
                    color: STYLE_VARS.textColor,
                    textTransform: 'uppercase',
                  }}
                >
                  Filter Notices
                </Text>
              </View>
              <View style={{ flexDirection: 'column' }}>
                {noticeCategories.map((category) => (
                  <TouchableOpacity
                    style={[styles.selectWrapper, { alignSelf: 'flex-start' }]}
                    key={category.id}
                    onPress={() => handleFilterSelect(category)}
                  >
                    <View style={styles.selectedBox}>
                      {filterSelected.includes(category) && (
                        <View
                          style={{
                            width: '100%',
                            height: '100%',
                            borderColor: STYLE_VARS.primaryColor,
                            borderWidth: 5,
                          }}
                        ></View>
                      )}
                    </View>
                    <Text style={styles.selectedText}>{decode(category.name)}</Text>
                  </TouchableOpacity>
                ))}
                {filterSelected.map((selected) => {
                  if (selected.child && selected.child.length > 0) {
                    return (
                      <View key={selected.id}>
                        <View
                          style={{ flexDirection: 'row', marginBottom: 20 }}
                        >
                          <View>
                            <Image
                              source={
                                IS_WEB
                                  ? addBase64Prefix(filter_button)
                                  : require('../../assets/phone-white.png')
                              }
                              style={{ width: 17, height: 17, marginRight: 8 }}
                            />
                          </View>
                          <Text style={styles.selectedText}>
                            ALL {decode(selected.name)}
                          </Text>
                        </View>
                        {selected.child.map((filter) => (
                          <TouchableOpacity
                            style={[
                              styles.selectWrapper,
                              { alignSelf: 'flex-start' },
                            ]}
                            key={filter.id}
                            onPress={() => handleFilterSelect(filter)}
                          >
                            <View style={styles.selectedBox}>
                              {filterSelected.includes(filter) && (
                                <View
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    borderColor: STYLE_VARS.primaryColor,
                                    borderWidth: 5,
                                  }}
                                ></View>
                              )}
                            </View>
                            <Text style={styles.selectedText}>
                              {decode(filter.name)}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    );
                  }
                })}
                <TouchableOpacity
                  style={[styles.selectWrapper, { alignSelf: 'flex-start' }]}
                  onPress={() => handleFilterReset()}
                >
                  <Image
                    source={
                      IS_WEB
                        ? addBase64Prefix(reset_icon)
                        : require('../../assets/reset-icon.png')
                    }
                    style={{
                      width: 17,
                      height: 17,
                      marginRight: 8,
                    }}
                  />
                  <Text style={styles.selectedText}>RESET FILTER</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
          <View style={{ width: '75%' }}>
            <PageTitle
              title="Club Notices"
              icon={require('../../assets/notices.png')}
              haveBottomLine={false}
              haveGoBackButton={haveGoBackButton}
            />
            <View>{children}</View>
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

export default NoticeLayout;

const styles = StyleSheet.create({
  selectWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  selectedText: {
    fontSize: 15,
    fontFamily: FONTS.AvenirMedium,
    color: STYLE_VARS.textColor,
    fontWeight: '500',
    textTransform: 'uppercase',
  },
  selectedBox: {
    width: 17,
    height: 17,
    borderWidth: 0.5,
    borderColor: STYLE_VARS.fccDarkGrey,
    borderRadius: 2,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
